<script lang="ts">
	import { beforeUpdate, createEventDispatcher, setContext } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		Button,
		Modal,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system';
	import { edit } from 'src/stores/ui/edit_lists';
	import { scenes } from 'src/stores/lists/scenes';
	import {
		setWizardSteps,
		scrollToElement,
		updateWizardStepOnPrevNext,
	} from '../../Lists/list-helpers';
	import WhenStep from './Steps/WhenStep.svelte';
	import DoStep from './Steps/DoStep.svelte';
	import MainStep from './Steps/MainStep.svelte';
	import ActiveTimeStep from './Steps/ActiveTimeStep.svelte';
	import MoreSettingsStep from './Steps/MoreSettingsStep.svelte';

	// import { scene_object_prototype } from './scene_object';
	import { postReq } from '../../../services/http';
	import { validate_scene } from '../helpers/validators';
	import { capFirstChar, _deepClone, deepCopy } from '../../../helpers/utilities';
	import { check_for_broken_conditions_or_actions } from '../helpers/validators';
	import SelectRoomStep from './Steps/SelectRoomStep.svelte';
	import type { ISceneObj } from '../../../types/SceneType';
	import { scene_object_prototype } from './scene_object';
	import { push } from 'svelte-spa-router';
	import { writable } from 'svelte/store';
	import { rooms } from 'src/stores/lists/rooms';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import { openDrawer } from 'src/helpers/drawer_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { onItemSaved, orderItemsInRoom } from 'src/helpers/add-entity-helpers';

	export let id,
		showAddEditModal = false,
		scene_object: ISceneObj;

	const dispatch = createEventDispatcher();
	const dispatchSceneSaved = createEventDispatcher<{ 'scene-saved': ISceneObj }>();

	const keyPress = (e) => {
		e.stopImmediatePropagation();
	};
	$: if (showAddEditModal) {
		window.addEventListener('keydown', keyPress);
	} else {
		window.removeEventListener('keydown', keyPress);
	}
	/**
	 * TODO: CHECK it twice
	 * - it uses a writable store to set , get and change context object for the Scene Wizard
	 */
	// setContext('scene_object', { scene_object: scene_object_prototype });
	// const { scene_object }: any = getContext('scene_object');

	let isGoToPremium = writable(false);
	setContext('go_to_premium', isGoToPremium);

	let allRoomsRefs: { [key: string | number]: HTMLElement } = {};
	let selectedRoomRef: HTMLElement = null;
	let wizardItemsContainerRef: HTMLElement = null;
	let modalRef: Modal = null;
	let scrolledToView = false;

	let removeContainerEventListener = () => {};

	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		scrolledToView = false;
		let elementRef = null;
		let wizardStepIndex = 0;

		if (activeStep.activeIndex === 4 && selectedRoomRef && !scrolledToView) {
			wizardStepIndex = 4;
			elementRef = selectedRoomRef;
		} else {
			wizardStepIndex = activeStep.activeIndex;
			elementRef = null;
		}

		if (!elementRef) {
			modalRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};

	let wizardComponents = [
		MainStep,
		SelectRoomStep,
		WhenStep,
		DoStep,
		ActiveTimeStep,
		MoreSettingsStep,
	];

	let wizardItems: any = setWizardSteps([
		capFirstChar($format('scene_preview_when')),
		capFirstChar($format('scene_preview_do')),
		capFirstChar($format('active_time')),
		capFirstChar($format('wizard_step_more_options', { default: 'More options' })),
		capFirstChar($format('select_room_step', { default: 'Select Room' })),
		capFirstChar($format('details_step', { default: 'details' })), // name and room,
	]).map((item, ind) => {
		return { ...item, component: wizardComponents[ind] };
	});
	let activeStep = wizardItems[0];

	const previousButton = $format('button_back', { default: 'Back' });

	$: isLastStepWizard = activeStep.activeIndex === Object.keys(wizardItems).length - 1;
	$: isFirstStep = activeStep.activeIndex === 0;

	$: nextButton = isLastStepWizard
		? $format('button_save', { default: 'Save' })
		: $format('button_next', { default: 'Next' });

	const sceneTitle = scene_object?._id
		? $format('prompt_title_edit_scene', {
				default: 'Edit scene',
			})
		: $format('prompt_title_add_scene', { default: 'Add scene' });

	const onClickWizardItem = (e, index: number) => {
		wizardItems = wizardItems.map((item, i) => {
			if (i === index) {
				activeStep = {
					...item,
					activeIndex: i,
				};

				return {
					...item,
					active: true,
				};
			}

			return {
				...item,
				active: false,
			};
		});

		scrollSelectedCardToView();
	};

	let hideFooter = false;
	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	let clonedSceneObject;

	if (scene_object?.id) {
		clonedSceneObject = deepCopy(scene_object);
	}

	let wizardStepValidation = {
		0:
			scene_object?.if &&
			(scene_object?.if?.or?.length > 0 ||
				scene_object?.if?.and?.find((el) => el.or && !el?.or?.find((e) => e.indailyint))),
		1: scene_object?.do?.length > 0,
		2:
			scene_object?.if?.or ||
			scene_object?.if?.and.find((el) => el?.indailyint) ||
			scene_object?.if?.and?.find((el) => el.or && el?.or?.every((e) => e.indailyint)),
		3: typeof scene_object?._enabled !== 'undefined',
		4: scene_object?._meta?.room !== '',
		5:
			typeof scene_object?._meta?.name !== 'undefined' &&
			scene_object?._meta?.name?.trim() !== '' &&
			(scene_object?._meta?.image !== '' || scene_object?._meta?.backgroundColor !== ''),
	};
	const clearData = () => {
		scene_object = _deepClone(scene_object_prototype) as ISceneObj;
		id = undefined;
	};

	let process_request = false;

	const isNewSceneOrRoomChanged = (clonedScene: ISceneObj, scene: ISceneObj): boolean => {
		const existing = $scenes[clonedScene?._id];
		return !existing || Number(existing?._meta?.room) !== Number(scene?._meta?.room);
	};

	const save_scene = async (scene) => {
		// console.log(JSON.stringify(scene, null, 2));

		scene._meta.room = scene._meta.room == '' ? -1 : scene._meta.room;
		// write in the room name (roomn) so that the mail sent by the scene can reference it.
		// ToDo: add translations for General and missing.
		scene._meta.roomn =
			typeof $rooms[scene._meta.room] !== 'undefined'
				? $rooms[scene._meta.room].name
				: scene._meta.room == -1
					? 'General'
					: 'Missing';

		scene._meta.name = scene._meta.name.trim();
		// on add new scene update position property
		// or if user changed room
		// console.log(
		// 	$scenes[scene?._id],
		// 	Number($scenes[scene?._id]?._meta?.room),
		// 	Number(scene?._meta.room)
		// );
		if (isNewSceneOrRoomChanged(clonedSceneObject, scene)) {
			await orderItemsInRoom(scene, 'scenes');
			scene._meta.position = 0; // New/changed room  items should be added at the first position to eliminate the need to scroll when adding a new item
		}

		if (
			typeof scene.name !== 'undefined' ||
			typeof scene.position !== 'undefined' ||
			typeof scene.room !== 'undefined' ||
			typeof scene.id !== 'undefined' ||
			typeof scene.image !== 'undefined' ||
			typeof scene.backgroundColor !== 'undefined'
		) {
			delete scene.position;
			delete scene.id;
			delete scene.room;
			delete scene.name;
			delete scene.image;
			delete scene.backgroundColor;
		}

		let res = {};

		if (scene._id === undefined && !$edit) {
			//we save new scene
			res = await postReq('/scene/add', {
				scene_script: JSON.stringify(scene),
			});
			if (res.isok && res?.data?.scene_id) {
				scene._id = res.data.scene_id;
				// console.log('saved ', scene);
				dispatchSceneSaved('scene-saved', scene);
				// When adding a new item, set the temporary prop `new` to show the pulse animation
				return {
					...scene,
					_meta: {
						...(scene._meta ?? {}),
						new: true,
					},
				};
			}
		}

		if (scene._id) {
			//we edit a scene
			res = await postReq('/scene/edit', {
				id: scene._id,
				scene_script: JSON.stringify(scene),
			});
			if (res.isok && res?.data?.scene_id) {
				// console.log('edit', scene);
				dispatchSceneSaved('scene-saved', scene);
				return scene;
			}
		}
	};

	const onCloseEditModal = () => {
		clearData();
		dispatch('close');
		showAddEditModal = false;
	};

	const handleSave = () => {
		if (validate_scene(scene_object) && !check_broken()) {
			save_scene(scene_object)
				.then((scene) => {
					// console.log('Saved scene on server ', JSON.stringify(scene, null, 2));

					if (isNewSceneOrRoomChanged(clonedSceneObject, scene)) {
						onItemSaved(scene, 'scenes');
					}

					if (!$edit) {
						// scenes.update(scene);
						scenes.addScene(scene);
					} else if ($edit) {
						scenes.editScene(scene);
					}

					openDrawer('scene', scene?._id);
					onCloseEditModal();
				})
				.catch((err) => {
					console.log(err);
					// toast.open({type:'danger',title: $format('error_global')});
					// process_request = false;
				});
		} else {
			console.log('return false');
			process_request = false;
		}
	};

	const check_broken = () => {
		if (scene_object._meta.broken) {
			const isBroken = check_for_broken_conditions_or_actions(scene_object);
			if (isBroken) {
				return true;
			} else {
				//This must be observed if works as expected
				scene_object._meta.broken = false;
				return false;
			}
		}
	};

	const onFinishCardEdit = async () => {
		await handleSave();
		onCloseEditModal();
	};
	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };

		console.log('prev next scroll call ');
		scrollSelectedCardToView();
	};

	beforeUpdate(() => {
		wizardStepValidation = {
			0:
				scene_object?.if &&
				(scene_object?.if?.or?.length > 0 ||
					!!scene_object?.if?.and?.find((el) => el.or && !el?.or?.find((e) => e.indailyint))),
			1: scene_object?.do?.length > 0,
			2:
				scene_object?.if?.or?.length > 0 ||
				!!scene_object?.if?.and?.find((el) => el?.indailyint) ||
				!!scene_object?.if?.and?.find((el) => el.or && el?.or?.every((e) => e.indailyint)),
			3: typeof scene_object?._enabled !== 'undefined',
			4: scene_object?._meta?.room !== '',
			5:
				typeof scene_object?._meta?.name !== 'undefined' &&
				scene_object?._meta?.name?.trim() !== '' &&
				(scene_object?._meta?.image !== '' || scene_object?._meta?.backgroundColor !== ''),
		};

		if ($isGoToPremium) {
			push('/settings/premium');
			showAddEditModal = false;
		}
	});

	const isWizardItemDisabled = (wizardItem, wizardStepValidation) => {
		const currentStepIndex = wizardItem.stepNumber - 1;

		const firstInvalidWizardItemIndex = Object.values(wizardStepValidation).findIndex((v) => !v);

		const isAllWizardStepsValid = firstInvalidWizardItemIndex === -1;

		// Not disabled active wizard step
		if (wizardItem.active) {
			return false;
		}

		// Not disabled all steps if they are all valid
		if (isAllWizardStepsValid) {
			return false;
		}

		// Not disabled all steps before first invalid found
		if (!isAllWizardStepsValid && currentStepIndex <= firstInvalidWizardItemIndex) {
			return false;
		}

		// Disable all steps by default
		return true;
	};
</script>

<Modal
	on:close={() => {
		if (
			(!scene_object?.id && scene_object?.if?.or?.length > 0) ||
			(scene_object?.id && JSON.stringify(scene_object) !== JSON.stringify(clonedSceneObject))
		) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			hideFooter = false;
			onCloseEditModal();
		}
	}}
	showModal={showAddEditModal}
	title={sceneTitle}
	hideSlotFooter={hideFooter}
	bind:this={modalRef}
	removeScrollPadding
>
	<!-- <svelte:fragment slot="toolbar"> -->
	<Wizard
		activeStepNumber={activeStep.stepNumber}
		stepsTotal={wizardItems.length}
		activeStepTitle={activeStep.title}
		slot="toolbar"
	>
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={(e) => {
					onClickWizardItem(e, i);
				}}
				active={item.active}
				title={item.title}
				disabled={isWizardItemDisabled(item, wizardStepValidation)}
				showMoreDots={item.showMoreDots}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>
	<!-- </svelte:fragment> -->

	<div class:flex-overflow-hidden={hideFooter} slot="content">
		<WizardContainer
			bind:wizardItemsContainerRef
			activeStepIndex={activeStep.activeIndex}
			adaptiveHeight
			fullHeight={hideFooter}
		>
			{#each wizardItems as item, i (i)}
				<!-- DO NOT REMOVE COMMENT: Padding references -->
				<WizardContent active={activeStep.activeIndex === i} class="pl-0 pr-0 pb-s pt-s">
					{#if i === 0}
						<WhenStep
							stopConfirmationModal
							bind:scene_object
							on:hide_footer={(e) => (hideFooter = e.detail.hideFooter)}
						/>
					{:else if i === 1}
						<DoStep
							stopConfirmationModal
							bind:scene_object
							on:hide_footer={(e) => (hideFooter = e.detail.hideFooter)}
						/>
					{:else if i === 2}
						<ActiveTimeStep stopConfirmationModal bind:scene_object />
					{:else if i === 3}
						<MoreSettingsStep bind:scene_object />
					{:else if i === 4}
						<SelectRoomStep
							bind:allRoomsRefs
							bind:scene_object
							bind:selectedRef={selectedRoomRef}
						/>
					{:else}
						<MainStep bind:scene_object />
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if !isFirstStep}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					onClickPrevNextButton('prev');
				}}
			>
				{previousButton}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			disabled={typeof wizardStepValidation === 'object'
				? !wizardStepValidation[activeStep.activeIndex]
				: isLastStepWizard}
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => {
				if (isLastStepWizard) {
					onFinishCardEdit();
				} else {
					onClickPrevNextButton('next');
				}
			}}
		>
			{nextButton}
		</Button>
	</div>
</Modal>

<ExitConfirmationModal
	type={$format('the_scene', { default: 'the scene' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	{showAddEditModal}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		dispatch('close');
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		showAddEditModal = e.detail.showAddEditModal;
	}}
/>
